// src/lib/opportunity/proposalTextResolver.ts
//
// Lookup helper for proposal text overrides at render time. Returns
// per-section body copy and an `included` flag so the PDF generator
// can omit whole sections when a manager unchecked them.
//
// Resolution precedence:
//   1. Product-type-specific row
//   2. Generic template row (`product_type = '*'`)
//   3. Hard-coded default (included = true)

import { listProposalTextOverrides, type ProposalTextOverride } from "./proposalTextClient";
import {
  PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD,
  PROPOSAL_TEXT_SECTION_KEYS,
  getProposalTextDefault,
  type ProposalTextSectionKey,
} from "./proposalTextSections";
import type { OpportunityFormatKey } from "@/types/opportunity";

export type ResolvedProposalSection = {
  body: string;
  included: boolean;
};

export type ResolvedProposalSections = Record<ProposalTextSectionKey, ResolvedProposalSection>;

export type ResolvedProposalText = Record<ProposalTextSectionKey, string>;

function pickOverride(
  overrides: ProposalTextOverride[],
  formatKey: OpportunityFormatKey,
  productType: string,
  sectionKey: ProposalTextSectionKey,
): ProposalTextOverride | null {
  return (
    overrides.find(
      (o) =>
        o.format_key === formatKey &&
        o.product_type === productType &&
        o.section_key === sectionKey,
    ) ??
    overrides.find(
      (o) =>
        o.format_key === formatKey &&
        o.product_type === PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD &&
        o.section_key === sectionKey,
    ) ??
    null
  );
}

/**
 * Picks the most-specific override for each section_key, falling back
 * to the format-aware hard-coded default.
 */
export function selectProposalSections(args: {
  overrides: ProposalTextOverride[];
  formatKey: OpportunityFormatKey;
  productType: string;
}): ResolvedProposalSections {
  const { overrides, formatKey, productType } = args;
  const result = {} as ResolvedProposalSections;
  for (const sectionKey of PROPOSAL_TEXT_SECTION_KEYS) {
    const row = pickOverride(overrides, formatKey, productType, sectionKey);
    if (row) {
      result[sectionKey] = { body: row.body, included: row.included };
      continue;
    }
    result[sectionKey] = {
      body: getProposalTextDefault(formatKey, sectionKey),
      included: true,
    };
  }
  return result;
}

/** Body-only map for callers that only need copy. */
export function selectProposalTexts(args: {
  overrides: ProposalTextOverride[];
  formatKey: OpportunityFormatKey;
  productType: string;
}): ResolvedProposalText {
  const sections = selectProposalSections(args);
  const result = {} as ResolvedProposalText;
  for (const key of PROPOSAL_TEXT_SECTION_KEYS) {
    result[key] = sections[key].body;
  }
  return result;
}

export async function resolveProposalSections(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
}): Promise<ResolvedProposalSections> {
  const { formatKey, productType } = args;
  const [specific, wildcard] = await Promise.all([
    listProposalTextOverrides({ format_key: formatKey, product_type: productType }),
    listProposalTextOverrides({
      format_key: formatKey,
      product_type: PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD,
    }),
  ]);
  const overrides: ProposalTextOverride[] = [
    ...(specific.ok ? specific.data.overrides : []),
    ...(wildcard.ok ? wildcard.data.overrides : []),
  ];
  return selectProposalSections({ overrides, formatKey, productType });
}

/** @deprecated Prefer `resolveProposalSections` for PDF generation. */
export async function resolveProposalTexts(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
}): Promise<ResolvedProposalText> {
  const sections = await resolveProposalSections(args);
  const result = {} as ResolvedProposalText;
  for (const key of PROPOSAL_TEXT_SECTION_KEYS) {
    result[key] = sections[key].body;
  }
  return result;
}
